import React from "react"
import PropTypes from "prop-types"
import moment from "moment"
import { getPath } from "../../lib"
import Grid from "./Grid"
import ValueRange from "./ValueRange"
import Time from "./Time"
import Period from "./Period"
import Date from "./Date"

export default class Observations extends React.Component {
    static propTypes = {
        resources: PropTypes.arrayOf(PropTypes.object)
    };

    getObservationLabel(o) {
        return (
            getPath(o, "code.coding.0.display") ||
            getPath(o, "code.text") ||
            getPath(o, "valueQuantity.code")
        );
    }

    getObservationNumericValue(o) {
        const quantityValue = getPath(o, "valueQuantity.value");
        if (!isNaN(parseFloat(quantityValue))) {
            return parseFloat(quantityValue);
        }

        const stringValue = o && o.hasOwnProperty("valueString") ? String(o.valueString).trim() : "";
        if (stringValue && /^-?\d+(\.\d+)?$/.test(stringValue)) {
            return parseFloat(stringValue);
        }

        return null;
    }

    isOutOfReferenceRange(o, range) {
        const value = this.getObservationNumericValue(o);
        if (value === null || !range) {
            return false;
        }

        const low = getPath(range, "low.value");
        const high = getPath(range, "high.value");

        if (!isNaN(parseFloat(low)) || !isNaN(parseFloat(high))) {
            const min = !isNaN(parseFloat(low)) ? parseFloat(low) : null;
            const max = !isNaN(parseFloat(high)) ? parseFloat(high) : null;

            if (min !== null && value < min) return true;
            if (max !== null && value > max) return true;
            return false;
        }

        const text = String(getPath(range, "text") || "").trim();
        const match = text.match(/^([<>]=?)\s*(-?\d+(\.\d+)?)$/);
        if (!match) {
            return false;
        }

        const operator = match[1];
        const limit = parseFloat(match[2]);

        if (operator === "<") return !(value < limit);
        if (operator === "<=") return !(value <= limit);
        if (operator === ">") return !(value > limit);
        if (operator === ">=") return !(value >= limit);

        return false;
    }

    isObservationOutOfAnyRange(o) {
        const ranges = Array.isArray(o.referenceRange) ? o.referenceRange : [];
        return ranges.some(range => this.isOutOfReferenceRange(o, range));
    }

    renderInterpretation(o) {
        const interpretations = Array.isArray(o.interpretation) ? o.interpretation : [];
        if (!interpretations.length) {
            return <span className="text-muted">-</span>;
        }

        const values = interpretations
            .map(item =>
                getPath(item, "coding.0.display") ||
                getPath(item, "coding.0.code") ||
                item.text
            )
            .filter(Boolean);

        if (!values.length) {
            return <span className="text-muted">-</span>;
        }

        return <span>{values.join(", ")}</span>;
    }

    renderReferenceRangeText(range) {
        if (!range) return "-";

        const low = getPath(range, "low.value");
        const high = getPath(range, "high.value");
        const unit = getPath(range, "low.unit") || getPath(range, "high.unit") || "";

        const hasLow = !isNaN(parseFloat(low));
        const hasHigh = !isNaN(parseFloat(high));

        if (hasLow && hasHigh) {
            return `${parseFloat(low)}-${parseFloat(high)}${unit ? ` (${unit})` : ""}`;
        }
        if (hasLow) {
            return `>=${parseFloat(low)}${unit ? ` (${unit})` : ""}`;
        }
        if (hasHigh) {
            return `<=${parseFloat(high)}${unit ? ` (${unit})` : ""}`;
        }

        return getPath(range, "text") || "-";
    }

    renderReferenceRange(o) {
        const ranges = Array.isArray(o.referenceRange) ? o.referenceRange : [];
        if (!ranges.length) {
            return <span className="text-muted">-</span>;
        }

        return (
            <span>
                {ranges.map((range, i) => {
                    return (
                        <span key={i}>
                            {i > 0 && ", "}
                            {this.renderReferenceRangeText(range)}
                        </span>
                    );
                })}
            </span>
        );
    }

    renderObservation(o, includeLabel = false) {
        if (Array.isArray(o.component)) {
            return o.component.map((c, i) => {
                let result = this.renderObservation(c, true);
                return (
                    <span key={i}>
                        {i > 0 && <span>,&nbsp;</span>}
                        {result}
                    </span>
                );
            });
        }

        const returnResult = result => {
            const outOfRange = this.isObservationOutOfAnyRange(o);
            return (
                <span>
                    {includeLabel && <label className="text-muted">{
                        this.getObservationLabel(o)
                            .replace(/^\s*(Systolic|Diastolic)\s+blood\s+pressure\s*$/gi, "$1")
                    }:&nbsp;</label>}
                    <span className={outOfRange ? "text-danger" : ""} style={{ fontWeight: outOfRange ? "700" : "400" }}>{result}</span>
                </span>
            );
        };

        // valueBoolean
        if (o.hasOwnProperty("valueBoolean")) {
            return returnResult(
                !o.valueBoolean || o.valueBoolean == "false" ?
                    "Negative" :
                    "Positive"
            );
        }

        // valueString
        if (o.hasOwnProperty("valueString")) {
            return returnResult(String(o.valueString));
        }

        // valueRange
        if (o.hasOwnProperty("valueRange")) {
            return returnResult(<ValueRange range={o.valueRange} />)
        }

        // valueTime
        if (o.hasOwnProperty("valueTime")) {
            return returnResult(<Time moment={o.valueTime} />)
        }

        // valueDateTime
        if (o.hasOwnProperty("valueDateTime")) {
            return returnResult(<Date moment={o.valueDateTime} />)
        }

        // valuePeriod
        if (o.hasOwnProperty("valuePeriod")) {
            return returnResult(Period(o.valuePeriod));
        }

        // valueCodeableConcept
        if (o.hasOwnProperty("valueCodeableConcept")) {
            return returnResult(getPath(o, "valueCodeableConcept.coding.0.display"));
        }

        // valueQuantity
        if (o.hasOwnProperty("valueQuantity")) {
            let value = getPath(o, "valueQuantity.value");
            let units = getPath(o, "valueQuantity.unit");

            if (!isNaN(parseFloat(value))) {
                value = Math.round(value * 100) / 100;
            }

            return returnResult(
                // <span>{value} <span className="text-muted">{units}</span></span>
                <span>{value} <span>{units}</span></span>
            );
        }

        /* TODO:
        valueRatio      : Ratio
        valueSampledData: SampledData
        valueAttachment : Attachment
        */

        return returnResult(<span className="text-muted">N/A</span>)
    }

    render() {
        return (
            <Grid
                rows={(this.props.resources || []).map(o => o.resource)}
                title="Observations"
                groupBy="Name"
                comparator={(a, b) => {
                    let dA = getPath(a, "effectivePeriod.start") || getPath(a, "effectiveDateTime") || getPath(a, "meta.lastUpdated");
                    let dB = getPath(b, "effectivePeriod.start") || getPath(b, "effectiveDateTime") || getPath(b, "meta.lastUpdated");
                    dA = dA ? +moment(dA) : 0;
                    dB = dB ? +moment(dB) : 0;
                    return dB - dA;
                }}
                cols={[
                    {
                        label: "Name",
                        path: o => this.getObservationLabel(o),
                        render: o => <b>{this.getObservationLabel(o)}</b>
                    },
                    {
                        label: "Value",
                        render: o => this.renderObservation(o)
                    },
                    {
                        label: "Interpretation",
                        render: o => this.renderInterpretation(o)
                    },
                    {
                        label: "Reference Range",
                        render: o => this.renderReferenceRange(o)
                    },
                    {
                        label: "Date",
                        render: o => {
                            let date = getPath(o, "effectivePeriod.start") || getPath(o, "effectiveDateTime") || getPath(o, "meta.lastUpdated");
                            if (date) date = moment(date).format("MM/DD/YYYY");
                            return <div className="text-muted">{date || "-"}</div>
                        }
                    }
                ]}
            />
        )
    }
}
